import { useRef, useState } from 'react';
import { SendIcon, TrashIcon } from '../../components/icons';
import { InlineSpinner } from '../../components/common/spinner';
import { Dependent, useInviteDependents } from '../../hooks/api/useInviteDependents';
import { DependentCard } from '../../components/invite-dependents/dependent-card';
import { RemoveDependentModal } from '../../components/invite-dependents/remove-dependent-modal';
import { InviteDependentsForm } from '../../components/invite-dependents/invite-dependents-form';
import { normalizePhoneNumber } from '../../utils/normalize-phone-input';
import { FormBlock } from '../../components/modals/settings-modal/form-block';
import { PageHeader } from '../../components/modals/settings-modal/page-header';
import Toast from '../../components/common/pop-up-message';
import useAuth from '../../hooks/utils/useAuth';

export const InviteDependents = () => {
  const toastNotificationContainerRef = useRef<HTMLDivElement>(null);
  const [dependentToRemove, setDependentToRemove] = useState<Dependent | undefined>(undefined);
  const { user, isLoading: userIsLoading } = useAuth();
  const { data: inviteDependents, isLoading: isInviteDependentsLoading, resendInvite } = useInviteDependents(user?.id);

  const isLoading = userIsLoading || isInviteDependentsLoading;
  const maxNumDependents = user?.caller_role?.active_subscription?.package.dependents_configuration.dependent_count;
  const canUserAddDependents =
    user?.caller_role?.active_subscription?.package.dependents_configuration.members_can_add_dependents;

  const resendInviteDependent = (dependentId: number) => {
    return new Promise((res) =>
      resendInvite.mutate(dependentId, {
        onSuccess: () => {
          Toast.success('Invite email sent again!', undefined, toastNotificationContainerRef?.current);
          res(null);
        },
        onError: (error: any) => {
          // @ts-ignore
          const errorMessage = error?.response?.data?.description;
          if (errorMessage) {
            Toast.error(
              `There was an error resending the invite. ${errorMessage ? errorMessage : 'Please contact your administrator.'}`,
              undefined,
              toastNotificationContainerRef?.current,
            );
          }
          res(error);
        },
      }),
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <InlineSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div ref={toastNotificationContainerRef} />
      <PageHeader
        header="Invite dependents"
        subHeader="Your plan provides access for up to 3 dependents. You can invite them below to access your benefit."
      />

      {inviteDependents && inviteDependents.length > 0 && (
        <FormBlock header="My dependents" className="mt-6">
          <div className="space-y-4">
            {inviteDependents.map(({ id, first_name, last_name, email_address, mobile_phone, invite_status }) => (
              <DependentCard
                key={id}
                className="w-full"
                displayName={`${first_name} ${last_name}`}
                emailAddress={email_address}
                phoneNumber={normalizePhoneNumber(mobile_phone.replace('+1', ''))}
                pending={invite_status === 'pending'}
                menuItems={[
                  {
                    onClick: () => {
                      resendInviteDependent(id);
                    },
                    title: 'Resend',
                    icon: <SendIcon />,
                  },
                  {
                    onClick: () => {
                      setDependentToRemove({ id, first_name, last_name } as Dependent);
                    },
                    title: 'Remove',
                    icon: <TrashIcon />,
                  },
                ]}
              />
            ))}
          </div>
        </FormBlock>
      )}
      {inviteDependents?.length !== maxNumDependents && canUserAddDependents && (
        <div className="mt-6">
          <InviteDependentsForm
            className="space-y-3 rounded-lg border border-neutral-200 bg-white px-4 py-5"
            bottomActionButton={({ isDirty, isSubmitting }) => (
              <div className="mt-4 flex justify-end">
                <button
                  data-testid="dependent-send-invite-button"
                  type="submit"
                  disabled={!isDirty || isSubmitting}
                  className="text-primary font-open-sans bg-button-secondary hover:bg-button-secondary-hover inline-flex h-10 items-center justify-center gap-1.5 rounded-full px-6 py-2.5 text-base font-medium leading-5 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Send invitation'}
                </button>
              </div>
            )}
          />
        </div>
      )}
      <RemoveDependentModal
        open={!!dependentToRemove}
        dependent={dependentToRemove}
        onClose={() => setDependentToRemove(undefined)}
      />
    </div>
  );
};
